import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import Dialog from "components/dialog";
import InputFormField from "components/input-form-field";
import SelectFormField from "components/select-form-field";
import { US_STATES } from "constants/addresses";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import {
  useLazyCheckNpiQuery,
  useLazyNpiSearchQuery
} from "services/physicians";
import { NPPESSearchResult } from "services/types/physician.type";

import NPIDuplicateDialog from "./npi-duplicate-dialog.component";

const NPI_SEARCH_LIMIT = 20;

type SearchFormValues = {
  number: string;
  first_name: string;
  last_name: string;
  city: string;
  state: string;
};

const DEFAULT_SEARCH_VALUES: SearchFormValues = {
  number: "",
  first_name: "",
  last_name: "",
  city: "",
  state: ""
};

function parseRetrySeconds(error: any): number {
  const message = error?.data?.message || error?.data?.detail || "";
  const match = message.match(/(\d+)\s*second/);
  return match ? parseInt(match[1], 10) : 10;
}

type NPISearchModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect?: (data: NPPESSearchResult) => void | Promise<void>;
};

export default function NPISearchModal({
  open,
  onClose,
  onSelect
}: NPISearchModalProps) {
  const navigate = useNavigate();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);
  const [results, setResults] = useState<NPPESSearchResult[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentSkip, setCurrentSkip] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean;
    name: string;
    npi: string;
    physicianId: number;
  }>({ open: false, name: "", npi: "", physicianId: 0 });

  const [triggerSearch, { isFetching: isSearching }] = useLazyNpiSearchQuery();
  const [triggerCheckNpi] = useLazyCheckNpiQuery();

  const clinicState = String(
    currentClinic?.state || currentUser?.default_clinic?.state || ""
  );

  const { control, handleSubmit, getValues, reset, setValue } =
    useForm<SearchFormValues>({
      mode: "onChange",
      defaultValues: DEFAULT_SEARCH_VALUES
    });

  useEffect(() => {
    if (open) {
      setValue("state", clinicState);
    }
  }, [open, clinicState, setValue]);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setCooldown(seconds);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const performSearch = useCallback(
    async (skip: number) => {
      const values = getValues();
      const hasAtLeastOneField =
        values.number || values.first_name || values.last_name;
      if (!hasAtLeastOneField) {
        setErrorMessage(
          "Please enter at least one of: NPI Number, First Name, or Last Name."
        );
        return;
      }

      setErrorMessage("");
      try {
        const response = await triggerSearch(
          {
            ...(values.number && { number: values.number }),
            ...(values.first_name && { first_name: values.first_name }),
            ...(values.last_name && { last_name: values.last_name }),
            ...(values.city && { city: values.city }),
            ...(values.state && { state: values.state }),
            limit: NPI_SEARCH_LIMIT,
            skip
          },
          false
        ).unwrap();

        if (skip === 0) {
          setResults(response.results);
        } else {
          setResults((prev) => [...prev, ...response.results]);
        }
        setHasMore(response.count >= NPI_SEARCH_LIMIT);
        setCurrentSkip(skip);
        setHasSearched(true);
      } catch (error: any) {
        const statusCode = error?.status;
        if (statusCode === 429) {
          const seconds = parseRetrySeconds(error);
          startCooldown(seconds);
        } else if (statusCode === 400) {
          const data = error?.data;
          const detail = data?.detail || data?.message;
          const fieldErrors = Object.values(data || {})
            .flat()
            .join(". ");
          setErrorMessage(
            detail ||
              fieldErrors ||
              "Invalid search parameters. Please check your input."
          );
        } else if (statusCode === 503) {
          setErrorMessage(
            "NPI Registry is temporarily unavailable. Please try again in a moment."
          );
        } else if (statusCode >= 500) {
          setErrorMessage("Something went wrong on our end. Please try again.");
        } else if (statusCode === "FETCH_ERROR" || !statusCode) {
          setErrorMessage(
            "Unable to reach the server. Please check your connection."
          );
        } else {
          setErrorMessage("An unexpected error occurred. Please try again.");
        }
        setHasSearched(true);
      }
    },
    [getValues, triggerSearch, startCooldown]
  );

  const onSearch = useCallback(() => {
    performSearch(0);
  }, [performSearch]);

  const onLoadMore = useCallback(() => {
    performSearch(currentSkip + NPI_SEARCH_LIMIT);
  }, [performSearch, currentSkip]);

  const handleAddToSuno = useCallback(
    async (result: NPPESSearchResult) => {
      if (result.is_duplicate && result.existing_physician_id) {
        setDuplicateDialog({
          open: true,
          name: `${result.first_name} ${result.last_name}`,
          npi: result.npi,
          physicianId: result.existing_physician_id
        });
        return;
      }

      try {
        const checkResponse = await triggerCheckNpi(result.npi).unwrap();
        if (checkResponse.exists && checkResponse.physician) {
          setDuplicateDialog({
            open: true,
            name: `${checkResponse.physician.first_name} ${checkResponse.physician.last_name}`,
            npi: result.npi,
            physicianId: checkResponse.physician.id
          });
          return;
        }
      } catch {
        // If check fails, proceed to create anyway
      }

      if (onSelect) {
        await onSelect(result);
        onClose();
      } else {
        onClose();
        navigate("/settings/physicians/new", {
          state: {
            npiData: {
              first_name: result.first_name,
              last_name: result.last_name,
              suffix: result.suffix,
              npi: result.npi,
              practice_name: result.practice_name,
              street_address_1: result.street_address_1,
              street_address_2: result.street_address_2,
              city: result.city,
              state: result.state,
              zip_code: result.zip_code,
              phone: result.phone,
              fax_phone: result.fax_phone,
              npi_data_snapshot: result.npi_data_snapshot
            }
          }
        });
      }
    },
    [triggerCheckNpi, navigate, onClose, onSelect]
  );

  const handleClose = useCallback(
    (_event: object, _reason: string) => {
      setResults([]);
      setHasMore(false);
      setHasSearched(false);
      setErrorMessage("");
      setCurrentSkip(0);
      setCooldown(0);
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
      reset({ ...DEFAULT_SEARCH_VALUES, state: clinicState });
      onClose();
    },
    [onClose, reset, clinicState]
  );

  const isThrottled = cooldown > 0;

  return (
    <>
      <Dialog
        title="Search NPI Registry"
        open={open}
        onClose={handleClose}
        minWidth={1200}
        maxWidth="xl"
      >
        <Box sx={{ py: 2 }}>
          <form onSubmit={handleSubmit(onSearch)}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={1.5}>
                <InputFormField
                  name="number"
                  label="NPI"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^\d{0,10}$/,
                      message: "Must be up to 10 digits"
                    }
                  }}
                  sx={{ m: 0 }}
                />
              </Grid>
              <Grid item xs={2}>
                <InputFormField
                  name="first_name"
                  label="First Name"
                  control={control}
                  rules={{
                    minLength: {
                      value: 2,
                      message: "Min 2 characters"
                    }
                  }}
                  sx={{ m: 0 }}
                />
              </Grid>
              <Grid item xs={2}>
                <InputFormField
                  name="last_name"
                  label="Last Name"
                  control={control}
                  rules={{
                    minLength: {
                      value: 2,
                      message: "Min 2 characters"
                    }
                  }}
                  sx={{ m: 0 }}
                />
              </Grid>
              <Grid item xs={1.5}>
                <InputFormField
                  name="city"
                  label="City"
                  control={control}
                  sx={{ m: 0 }}
                />
              </Grid>
              <Grid item xs={2.5}>
                <SelectFormField
                  name="state"
                  label="State"
                  control={control}
                  options={US_STATES.getOptions()}
                  sx={{ m: 0 }}
                />
              </Grid>
              <Grid item xs={2} sx={{ mt: "auto" }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSearching}
                  disabled={isThrottled}
                  fullWidth
                  sx={{ height: 40, mt: "auto" }}
                >
                  {isThrottled ? `Wait ${cooldown}s` : "Search"}
                </LoadingButton>
              </Grid>
            </Grid>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Tip: Use * as a wildcard in name fields (e.g., &quot;Smi*&quot;
              matches Smith, Smithson, etc.)
            </Typography>
          </form>

          {errorMessage && (
            <Alert
              severity="warning"
              onClose={() => setErrorMessage("")}
              sx={{ mt: 1 }}
            >
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ mt: 2, maxHeight: 400, overflow: "auto" }}>
            {isSearching && results.length === 0 && (
              <CircularProgress sx={{ width: "100%" }} />
            )}

            {hasSearched &&
              !errorMessage &&
              results.length === 0 &&
              !isSearching && (
                <Typography
                  color="text.secondary"
                  textAlign="center"
                  sx={{ py: 4 }}
                >
                  No results found.
                </Typography>
              )}

            {results.length > 0 && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {results.length} result{results.length !== 1 ? "s" : ""}
                  {hasMore ? "+" : ""} found
                </Typography>
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }}>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>NPI</TableCell>
                        <TableCell>Specialty</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.map((result, index) => (
                        <TableRow key={result.npi} hover>
                          <TableCell sx={{ color: "text.secondary" }}>
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            {result.first_name} {result.last_name}
                            {result.suffix ? `, ${result.suffix}` : ""}
                          </TableCell>
                          <TableCell>{result.npi}</TableCell>
                          <TableCell>{result.taxonomy_description}</TableCell>
                          <TableCell>
                            {[
                              result.street_address_1,
                              result.city,
                              result.state,
                              result.zip_code
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </TableCell>
                          <TableCell>{result.phone}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={result.is_duplicate}
                              onClick={() => handleAddToSuno(result)}
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {result.is_duplicate
                                ? "Already in Suno"
                                : "Add to Suno"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>

          {hasMore && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <LoadingButton
                variant="outlined"
                size="small"
                loading={isSearching}
                disabled={isThrottled}
                onClick={onLoadMore}
              >
                {isThrottled ? `Wait ${cooldown}s` : "Load More"}
              </LoadingButton>
            </Stack>
          )}
        </Box>
      </Dialog>

      <NPIDuplicateDialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog((prev) => ({ ...prev, open: false }))}
        physicianName={duplicateDialog.name}
        npi={duplicateDialog.npi}
        existingPhysicianId={duplicateDialog.physicianId}
      />
    </>
  );
}
